import type { ElementConfig, StyleCustomization, ElementType } from './types';
import { SIZE_CONSTRAINTS, DEFAULT_STYLES } from './types';

/**
 * Validate element configuration
 * @param config - Element configuration to validate
 * @returns Validation result with any errors
 */
export function validateConfig(config: ElementConfig): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Check if type is valid
  if (!['opt-in', 'opt-out', 'settings'].includes(config.type)) {
    errors.push(`Invalid element type: ${config.type}. Must be 'opt-in', 'opt-out', or 'settings'`);
  }

  // Validate styles if provided
  if (config.styles) {
    const { styles } = config;
    
    // Validate width constraints
    if (styles.width !== undefined) {
      if (typeof styles.width !== 'number' || styles.width < SIZE_CONSTRAINTS.width.min || styles.width > SIZE_CONSTRAINTS.width.max) {
        errors.push(`Width must be a number between ${SIZE_CONSTRAINTS.width.min} and ${SIZE_CONSTRAINTS.width.max} pixels`);
      }
    }
    
    // Validate height constraints
    if (styles.height !== undefined) {
      if (typeof styles.height !== 'number' || styles.height < SIZE_CONSTRAINTS.height.min || styles.height > SIZE_CONSTRAINTS.height.max) {
        errors.push(`Height must be a number between ${SIZE_CONSTRAINTS.height.min} and ${SIZE_CONSTRAINTS.height.max} pixels`);
      }
    }
    
    // Validate radius constraints
    if (styles.radius !== undefined) {
      if (typeof styles.radius !== 'number' || styles.radius < SIZE_CONSTRAINTS.radius.min || styles.radius > SIZE_CONSTRAINTS.radius.max) {
        errors.push(`Radius must be a number between ${SIZE_CONSTRAINTS.radius.min} and ${SIZE_CONSTRAINTS.radius.max} pixels`);
      }
    }
    
    // Validate color format (basic hex validation)
    if (styles.textColor && !/^#[0-9A-F]{6}$/i.test(styles.textColor)) {
      errors.push('textColor must be a valid hex color (e.g., #FF0000)');
    }
    
    if (styles.backgroundColor && !/^#[0-9A-F]{6}$/i.test(styles.backgroundColor)) {
      errors.push('backgroundColor must be a valid hex color (e.g., #FF0000)');
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Get final styles for an element by merging defaults with user-provided styles
 * @param elementType - The type of element
 * @param userStyles - User-provided style customizations (optional)
 * @returns Final styles to be applied
 */
export function getFinalStyles(elementType: ElementType, userStyles?: StyleCustomization): StyleCustomization {
  const defaultStyles = DEFAULT_STYLES[elementType];
  const mergedStyles = { ...defaultStyles, ...userStyles };
  return sanitizeStyles(mergedStyles);
}

/**
 * Validate and sanitize style values
 * @param styles - Style customization to sanitize
 * @returns Sanitized styles with validated constraints
 */
export function sanitizeStyles(styles: StyleCustomization): StyleCustomization {
  const sanitized: StyleCustomization = {};
  
  if (styles.textColor) {
    sanitized.textColor = styles.textColor;
  }
  
  if (styles.backgroundColor) {
    sanitized.backgroundColor = styles.backgroundColor;
  }
  
  if (styles.radius !== undefined) {
    sanitized.radius = Math.max(SIZE_CONSTRAINTS.radius.min, Math.min(SIZE_CONSTRAINTS.radius.max, styles.radius));
  }
  
  if (styles.width !== undefined) {
    sanitized.width = Math.max(SIZE_CONSTRAINTS.width.min, Math.min(SIZE_CONSTRAINTS.width.max, styles.width));
  }
  
  if (styles.height !== undefined) {
    sanitized.height = Math.max(SIZE_CONSTRAINTS.height.min, Math.min(SIZE_CONSTRAINTS.height.max, styles.height));
  }
  
  return sanitized;
}
